/**
 * StationMap Component
 *
 * This component displays the geographical location of a weather station on an interactive map.
 * It shows the station's position with a circle indicating the Circular Error Probability (CEP),
 * which represents the accuracy of the station's location.
 *
 * Key Features:
 * - Interactive map using OpenStreetMap
 * - Station location marker with CEP circle
 * - Popup with detailed location information
 * - Responsive design
 * - Multilingual support through translation context
 *
 * @component
 * @example
 * <StationMap
 *   stationId="station123"
 *   latitude={45.4215}
 *   longitude={-75.6972}
 *   cep={100}
 *   metadata={{ name: "Station Name", Community: "Community Name" }}
 * />
 */

import React from "react";
import { MapContainer, TileLayer, Circle, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "../../context/TranslationContext";

/**
 * StationMap component that displays a weather station's location on a map
 * @param {Object} props - Component props
 * @param {string} props.stationId - Unique identifier for the station
 * @param {number} props.latitude - Station's latitude coordinate
 * @param {number} props.longitude - Station's longitude coordinate
 * @param {number} props.cep - Circular Error Probability in meters (location accuracy)
 * @param {Object} props.metadata - Station metadata including name and community
 * @returns {JSX.Element} The rendered StationMap component
 */
function StationMap({ stationId, latitude, longitude, cep, metadata }) {
  // Get translation function from context for multilingual support
  const { translate } = useTranslation();
  console.log(metadata);
  return (
    <Box sx={{ height: "400px", width: "100%" }}>
      {/* Station location header with translated text */}
      <Typography variant="h6" gutterBottom>
        {translate("Station Location")}: {translate(metadata.name)} (
        {translate(metadata.Community) || "N/A"})
      </Typography>

      {/* Interactive map container */}
      <MapContainer
        center={[latitude, longitude]}
        zoom={13}
        style={{ height: "calc(100% - 40px)", width: "100%" }}
      >
        {/* OpenStreetMap tile layer */}
        <TileLayer
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        {/* Station location marker with CEP circle */}
        <Circle center={[latitude, longitude]} radius={cep} color="red">
          {/* Popup with station location details */}
          <Popup>
            Latitude: {latitude}
            <br />
            Longitude: {longitude}
            <br />
            CEP: {cep} meters
          </Popup>
        </Circle>
      </MapContainer>
    </Box>
  );
}

export default StationMap;
